import { useState } from 'react';
import type { AiRuleDto } from '@konvert/shared';
import { t } from '../lib/i18n.js';

/**
 * The rules screen (SPEC 11.2, экран 8). Hard rules are enforced by code;
 * preferences only shape the assistant's wording, and that is said plainly.
 */
export function RulesScreen({
  rules,
  onAdd,
  onToggle,
}: {
  rules: AiRuleDto[];
  onAdd: (text: string) => void;
  onToggle: (rule: AiRuleDto) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const hard = rules.filter((rule) => rule.kind === 'constraint' && rule.status !== 'archived');
  const style = rules.filter((rule) => rule.kind === 'style' && rule.status !== 'archived');

  return (
    <div className="space-y-4">
      <section className="envelope space-y-3 p-4">
        <h1 className="text-lg text-ink">{t('rules.title')}</h1>

        <h2 className="text-xs uppercase tracking-wide text-ink-soft">{t('rules.hard')}</h2>
        {hard.length === 0 ? (
          <p className="text-sm text-ink-soft">{t('rules.empty')}</p>
        ) : (
          hard.map((rule) => <RuleRow key={rule.id} rule={rule} onToggle={onToggle} />)
        )}

        <h2 className="pt-2 text-xs uppercase tracking-wide text-ink-soft">{t('rules.style')}</h2>
        {style.length === 0 ? (
          <p className="text-sm text-ink-soft">{t('rules.empty')}</p>
        ) : (
          style.map((rule) => <RuleRow key={rule.id} rule={rule} onToggle={onToggle} />)
        )}
      </section>

      <div className="paper-card space-y-2 p-3">
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t('rules.placeholder')}
          className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-soft"
        />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!text.trim()}
            onClick={() => {
              onAdd(text.trim());
              setText('');
            }}
            className="rounded-[var(--radius-card)] bg-[var(--ink)] px-4 py-2 text-sm text-[var(--paper)] disabled:opacity-40"
          >
            {t('rules.add')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleRow({
  rule,
  onToggle,
}: {
  rule: AiRuleDto;
  onToggle: (rule: AiRuleDto) => void;
}): JSX.Element {
  return (
    <div className="paper-card flex items-start justify-between gap-3 px-3 py-2">
      <div className="min-w-0">
        <p className={`text-sm ${rule.status === 'active' ? 'text-ink' : 'text-ink-soft line-through'}`}>
          {rule.text}
        </p>
        <p className="mt-1 text-xs text-ink-soft">
          {rule.kind === 'style' ? t('rules.not_guaranteed') : 'проверяется кодом'}
          {rule.source_quote ? ` · «${rule.source_quote}»` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onToggle(rule)}
        className="shrink-0 text-xs text-ink-soft underline"
      >
        {rule.status === 'active' ? t('rules.disable') : t('rules.enable')}
      </button>
    </div>
  );
}
